import { Autocomplete } from '@features/common';
import { Autocomplete as MAutocomplete } from '@mui/material';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import {
	DEFAULT_PROCESS_CREATION_DATA,
	generateActualConsecutiveRules,
	generateFinalConsecutiveRules,
	INITIAL_CONSECUTIVE_RULES,
	PREFIX_RULES,
	PROCESS_NAME_FORMAT,
	TOPIC_PUSH_FORMAT,
} from '@features/processes/constants/process-forms.constants';
import ProcessCreationFormType from '@features/processes/interfaces/process-creation.interface';
import useFirebase from '@hooks/firebase.hook';
import { ClienteSuite, DivisionCliente } from '@interfaces/firestore-documents.interfaces';
import { Divider, Grid, Switch, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
	handleProcessNumberKeyPress,
	handleProcessNumberPaste,
} from '../ProcessEditionForm/ProcessEditionForm.handlers';
import { handleProcessNameChange } from './AddProcessForm.handlers';
import useAddProcessFormStyles from './AddProcessForm.styles';

interface Props {
	division: DivisionCliente;
	cities: Cities[];
	allData: ClienteSuite | null;
	form: UseFormReturn<ProcessCreationFormType>;
}

const AddProcessForm: React.FC<Props> = ({
	division: { combos: combosDivisionC, niveles_servicio: servicesDivision },
	form: { control, watch, formState, register, setError, setValue },
	cities,
	allData,
}) => {
	// Hooks
	const { combos, serviceLevels } = useFirebase();
	const classes = useAddProcessFormStyles();
	const [citie, setCitie] = useState<Cities | null | string>({
		nombre: '',
		codigo: '',
	});
	// Variables
	const combosDivision = combosDivisionC.map((c) => Number(c.id_combo));
	const clienteGeneraNumeracion = watch('cliente_genera_numeracion');
	const consecutivoDesde = watch('consecutivo_desde') || 0;
	const consecutivoHasta = watch('consecutivo_hasta') || 1;
	useEffect(() => {
		const wheel = document?.getElementsByClassName('numberInput');
		for (let index = 0; index < wheel.length; index++) {
			wheel[index].addEventListener(
				'wheel',
				function (e) {
					e.preventDefault();
				},
				{ passive: false }
			);
		}
	}, []);
	useEffect(() => {
		if (allData) {
			setValue('correo', '');
			setValue('telefono', allData.telefono_fijo);
			setValue('nombre', allData.nombre);
			setValue('direccion', allData.direccion_rango);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		if (allData && cities) {
			const filter = cities.filter(
				(i) => String(i.codigo_interno) === String(allData.codigo_municipio)
			);
			setCitie(filter[0]);
			setValue('ciudad', filter[0].codigo);
			setValue('nombre_ciudad', filter[0].nombre);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cities]);
	return (
		<>
			<h3 style={{ marginTop: 0 }} className={classes.h3}>
				Datos del cliente
			</h3>
			<Typography color="#707070" fontFamily="Montserrat" fontSize={14} fontWeight={500}>
				Nombre o Razón social
			</Typography>
			<TextField
				className={classes.textField}
				fullWidth
				error={Boolean(formState.errors.nombre)}
				helperText={formState.errors.nombre?.message}
				multiline
				size="medium"
				minRows={1}
				maxRows={5}
				style={{ marginTop: 8 }}
				type="text"
				{...register('nombre', {
					required: false,
				})}
			/>
			<Grid container spacing={3}>
				<Grid item md={6}>
					<Typography
						style={{ marginTop: 16 }}
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Ciudad
					</Typography>
					<Controller
						control={control}
						name="ciudad"
						rules={{
							required: false,
						}}
						render={({ fieldState }) => (
							<MAutocomplete
								options={cities}
								getOptionLabel={(option) => option.nombre}
								autoHighlight={true}
								style={{ marginTop: 8 }}
								value={citie}
								onChange={(_e, v) => {
									setError('ciudad', { message: undefined });
									if (typeof v === 'object') {
										setValue('ciudad', v?.codigo);
										setValue('nombre_ciudad', v?.nombre);
										setCitie(v);
									} else {
										setValue('ciudad', '');
										setValue('nombre_ciudad', '');
										setCitie({
											nombre: '',
											codigo: '',
										});
									}
								}}
								freeSolo
								renderInput={(params) => (
									<TextField
										{...params}
										className={classes.textField}
										size="medium"
										error={Boolean(fieldState.error?.message)}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						)}
					/>
				</Grid>
				<Grid item md={6}>
					<Typography
						style={{ marginTop: 16 }}
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Dirección
					</Typography>
					<TextField
						fullWidth
						error={Boolean(formState.errors.direccion)}
						helperText={formState.errors.direccion?.message}
						multiline
						size="medium"
						className={classes.textField}
						style={{ marginTop: 8 }}
						type="text"
						{...register('direccion', {
							required: false,
						})}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={3}>
				<Grid item md={6}>
					<Typography
						style={{ marginTop: 16 }}
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Teléfono
					</Typography>
					<TextField
						fullWidth
						error={Boolean(formState.errors.telefono)}
						className={classes.textField}
						helperText={formState.errors.telefono?.message}
						multiline
						size="medium"
						style={{ marginTop: 8 }}
						type="text"
						{...register('telefono', {
							required: false,
						})}
					/>
				</Grid>
				<Grid item md={6}>
					<Typography
						style={{ marginTop: 16 }}
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Correo electrónico
					</Typography>
					<TextField
						fullWidth
						error={Boolean(formState.errors.correo)}
						helperText={formState.errors.correo?.message}
						multiline
						className={classes.textField}
						size="medium"
						style={{ marginTop: 8 }}
						type="text"
						{...register('correo', {
							required: false,
						})}
					/>
				</Grid>
			</Grid>
			<Divider style={{ marginTop: 20 }}></Divider>
			<h3 className={classes.h3}>Datos del proceso</h3>
			<Typography color="#707070" fontFamily="Montserrat" fontSize={14} fontWeight={500}>
				Nombre del proceso
			</Typography>
			<Controller
				control={control}
				defaultValue={DEFAULT_PROCESS_CREATION_DATA.nombre_proceso}
				name="nombre_proceso"
				rules={{
					required: 'El Nombre del proceso es obligatorio',
					minLength: {
						value: 1,
						message: 'El Nombre del proceso es obligatorio',
					},
					pattern: {
						value: PROCESS_NAME_FORMAT,
						message: 'Se ingresó un caracter no permitido',
					},
					maxLength: {
						value: 100,
						message: 'El campo excede los 100 caracteres permitidos',
					},
				}}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<TextField
						fullWidth
						autoCapitalize="on"
						className={classes.textField}
						error={Boolean(error?.message)}
						helperText={
							error?.message ??
							'Solo se permiten estos caracteres especiales á . - _ ñ / ç'
						}
						onChange={handleProcessNameChange(onChange)}
						placeholder="Nombre del proceso"
						size="medium"
						sx={{ marginTop: '16px' }}
						value={value}
					/>
				)}
			/>
			<Divider style={{ marginTop: 20 }}></Divider>
			<h3 className={classes.h3}>Rango de guías y consecutivo</h3>
			<Grid container spacing={3}>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Prefijo
					</Typography>
					<Controller
						control={control}
						defaultValue={DEFAULT_PROCESS_CREATION_DATA.prefijo_consecutivo}
						name="prefijo_consecutivo"
						rules={PREFIX_RULES}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<TextField
								fullWidth
								className={`numberInput ${classes.textField}`}
								id="numberInput"
								error={Boolean(error?.message)}
								helperText={error?.message}
								inputProps={{ min: 0 }}
								onChange={onChange}
								onKeyPress={handleProcessNumberKeyPress}
								onPaste={handleProcessNumberPaste}
								placeholder="0000"
								size="medium"
								sx={{ marginTop: '8px' }}
								type="number"
								value={value}
							/>
						)}
					/>
				</Grid>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Inicial
					</Typography>
					<Controller
						control={control}
						name="consecutivo_desde"
						rules={INITIAL_CONSECUTIVE_RULES}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<TextField
								fullWidth
								error={Boolean(error?.message)}
								className={`numberInput ${classes.textField}`}
								helperText={error?.message}
								inputProps={{
									min: 0,
									max: 9999999,
								}}
								onChange={onChange}
								onKeyPress={handleProcessNumberKeyPress}
								onPaste={handleProcessNumberPaste}
								placeholder="9.999.999"
								size="medium"
								sx={{ marginTop: '8px' }}
								type="number"
								id="numberInput"
								value={value}
							/>
						)}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={3}>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
						style={{ marginTop: 16 }}
					>
						Final
					</Typography>
					<Controller
						control={control}
						name="consecutivo_hasta"
						rules={generateFinalConsecutiveRules(consecutivoDesde)}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<TextField
								fullWidth
								error={Boolean(error?.message)}
								className={`numberInput ${classes.textField}`}
								helperText={error?.message}
								inputProps={{ min: consecutivoDesde, max: 9999999 }}
								onChange={onChange}
								onKeyPress={handleProcessNumberKeyPress}
								onPaste={handleProcessNumberPaste}
								placeholder="9.999.999"
								size="medium"
								sx={{ marginTop: '8px' }}
								id="numberInput"
								type="number"
								value={value}
							/>
						)}
					/>
				</Grid>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
						style={{ marginTop: 16 }}
					>
						Actual
					</Typography>
					<Controller
						control={control}
						name="consecutivo_actual"
						rules={generateActualConsecutiveRules(
							clienteGeneraNumeracion,
							consecutivoDesde,
							consecutivoHasta
						)}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<TextField
								fullWidth
								className={`numberInput ${classes.textField}`}
								id="numberInput"
								disabled={clienteGeneraNumeracion}
								error={Boolean(error?.message)}
								helperText={error?.message}
								inputProps={{
									min: Number(consecutivoDesde) || 0,
									max: Number(consecutivoHasta) || 1,
								}}
								onChange={onChange}
								onKeyPress={handleProcessNumberKeyPress}
								onPaste={handleProcessNumberPaste}
								placeholder="9.999.999"
								size="medium"
								sx={{ marginTop: '8px' }}
								type="number"
								value={value}
							/>
						)}
					/>
				</Grid>
			</Grid>
			<Grid container alignItems="center" justifyContent="space-between">
				<Grid
					container
					alignItems="center"
					justifyContent="space-between"
					style={{ marginTop: 8 }}
					xs={5.65}
				>
					<Grid item>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Cliente genera numeración
						</Typography>
					</Grid>
					<Grid item>
						<Controller
							control={control}
							defaultValue={DEFAULT_PROCESS_CREATION_DATA.cliente_genera_numeracion}
							name="cliente_genera_numeracion"
							render={({ field: { onChange, value } }) => (
								<Switch
									checked={value}
									onChange={onChange}
									sx={{ marginRight: '-12px' }}
								/>
							)}
						/>
					</Grid>
				</Grid>
				<Grid
					container
					alignItems="center"
					justifyContent="space-between"
					style={{ marginTop: 8 }}
					xs={5.65}
				>
					<Grid item>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Habilitar referencia detalle única
						</Typography>
					</Grid>
					<Grid item>
						<Controller
							control={control}
							defaultValue={DEFAULT_PROCESS_CREATION_DATA.cliente_genera_numeracion}
							name="referencia_detalle_unica"
							render={({ field: { onChange, value } }) => (
								<Switch
									checked={value}
									onChange={onChange}
									sx={{ marginRight: '-12px' }}
								/>
							)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Typography
				color="#707070"
				fontFamily="Montserrat"
				fontSize={14}
				fontWeight={500}
				style={{ marginTop: 16 }}
			>
				Notificación push{' '}
				<Typography color="inherit" component="span" fontFamily="inherit" fontSize={12}>
					(Opcional)
				</Typography>
			</Typography>
			<Controller
				control={control}
				defaultValue={DEFAULT_PROCESS_CREATION_DATA.topic_push}
				name="topic_push"
				rules={{
					pattern: {
						value: TOPIC_PUSH_FORMAT,
						message: 'Se ingresó un caracter no permitido',
					},
					maxLength: {
						value: 50,
						message: 'El campo excede los 50 caracteres permitidos',
					},
				}}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<TextField
						fullWidth
						error={Boolean(error?.message)}
						helperText={
							error?.message ?? 'El campo solo recibe letras, números y guion bajo'
						}
						onChange={onChange}
						className={`${classes.textField}`}
						placeholder='push_tracking_"nombre_de_la_empresa"'
						size="medium"
						sx={{ marginTop: '16px' }}
						value={value}
					/>
				)}
			/>
			<Divider style={{ marginTop: 20 }}></Divider>
			<h3 className={classes.h3}>
				Servicios y Combos asociados{' '}
				<Typography color="inherit" component="span" fontFamily="inherit" fontSize={12}>
					(Opcional)
				</Typography>
			</h3>
			<Grid container spacing={3}>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Servicios
					</Typography>
					<Controller
						control={control}
						defaultValue={[]}
						name="servicios_asociados"
						render={({ field: { onChange, value } }) => (
							<Autocomplete
								className={`${classes.textField}`}
								getTooltipTitle={(value) => serviceLevels[value] ?? ''}
								id="service_level_option"
								options={Object.keys(serviceLevels)
									.map(Number)
									.filter((ns) => servicesDivision.includes(ns))}
								size="medium"
								onChange={onChange}
								placeholder="Servicios asociados"
								style={{ marginTop: 8 }}
								value={value ?? []}
							/>
						)}
					/>
				</Grid>
				<Grid item md={6}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Combos
					</Typography>
					<Controller
						control={control}
						defaultValue={[]}
						name="combos_asociados"
						render={({ field: { onChange, value } }) => (
							<Autocomplete
								className={`${classes.textField}`}
								getTooltipTitle={(value) => combos[value]?.nombre_combo ?? ''}
								id="combo_option"
								options={Object.values(combos)
									.filter(
										(c) =>
											c.estado && combosDivision.includes(Number(c.id_combo))
									)
									.map((c) => c.id_combo)}
								onChange={onChange}
								size="medium"
								placeholder="Combos asociados"
								style={{ marginTop: 8 }}
								value={value ?? []}
							/>
						)}
					/>
				</Grid>
			</Grid>
			<Divider style={{ marginTop: 20 }}></Divider>
			<h3 className={classes.h3}>
				Liquidación y Notificaciones{' '}
				<Typography color="inherit" component="span" fontFamily="inherit" fontSize={12}>
					(Opcional)
				</Typography>
			</h3>
			<Grid container alignItems="center" spacing={3}>
				<Grid item md={4}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Liquidación en guía
					</Typography>
				</Grid>
				<Grid item md={2}>
					<Controller
						control={control}
						defaultValue={DEFAULT_PROCESS_CREATION_DATA.liquidacion_en_guia}
						name="liquidacion_en_guia"
						render={({ field: { onChange, value } }) => (
							<div className={classes.switch}>
								<Switch checked={value} onChange={onChange} />
							</div>
						)}
					/>
				</Grid>
				<Grid item md={4}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Liquidación en despacho
					</Typography>
				</Grid>
				<Grid item md={2}>
					<Controller
						control={control}
						defaultValue={DEFAULT_PROCESS_CREATION_DATA.liquidacion_en_despacho}
						name="liquidacion_en_despacho"
						render={({ field: { onChange, value } }) => (
							<div className={classes.switch}>
								<Switch checked={value} onChange={onChange} />
							</div>
						)}
					/>
				</Grid>

				<Grid item md={4} style={{ paddingTop: 0 }}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Cotizador
					</Typography>
				</Grid>
				<Grid item md={2} style={{ paddingTop: 0 }}>
					<Controller
						control={control}
						defaultValue={DEFAULT_PROCESS_CREATION_DATA.cotizador}
						name="cotizador"
						render={({ field: { onChange, value } }) => (
							<div className={classes.switch}>
								<Switch checked={value} onChange={onChange} />
							</div>
						)}
					/>
				</Grid>
			</Grid>
		</>
	);
};

export default AddProcessForm;
